import { useEffect, useRef, useState } from 'react'
import { useNavigate, useLocation, Navigate } from 'react-router-dom'
import { Check, FileText, Upload } from 'lucide-react'
import { useCaseStore } from '../../stores'
import { useI18n } from '../../i18n'
import { CompactHeader } from '../../components/OrellaChatHeader'
import { referralRequestsAPI, aiAPI } from '../../services/api'
import { caseBack } from '../../utils/caseBack'
import { OrellaSpinner } from '../../components/OrellaLoader' // s123: the app's one spinner

/**
 * ReferralUploadScreen — s106, canvas "Orella Referrals Screens" A7 (upload) and A8 (on file). Copy
 * approved 2026-10-16. Route /case/referral-upload; state {doctorName}.
 *   ★ With an OPEN referral request on the case: the file goes to that request (POST
 *     /referral-requests/<id>/document), the document reader pre-fills the fields (/ai/extract-document
 *     on the stored key), and Save puts it on file (POST /referral-requests/<id>/answer). The API then
 *     writes the case authorization that check-in confirms (A9) and booking opens.
 *   ★ With NO open request nothing locks booking (the lock reads open requests only), so the patient's
 *     existing Authorizations & Referrals screen takes the file instead.
 */
const NAVY = '#15354F'
const MUTED = '#4E6577'
const card = { background: '#fff', border: '1px solid #DCE6EE', borderRadius: 16, padding: 14 }

export default function ReferralUploadScreen() {
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useI18n()
  const { currentCase } = useCaseStore()
  const fileRef = useRef(null)
  const [request, setRequest] = useState(undefined) // undefined = loading, null = none open
  const [phase, setPhase] = useState('pick') // pick | reading | review | saving | saved
  const [fileName, setFileName] = useState('')
  const [documentKey, setDocumentKey] = useState('')
  const [fields, setFields] = useState({ authNumber: '', referredTo: '', visits: '', validTo: '' })
  const [error, setError] = useState('')
  const doctorName = (location.state && location.state.doctorName) || ''

  useEffect(() => {
    if (!currentCase?.id) return undefined
    let alive = true
    referralRequestsAPI.listForCase(currentCase.id)
      .then((r) => { if (alive) setRequest((Array.isArray(r?.requests) ? r.requests : []).find((x) => x.status === 'requested') || null) })
      .catch(() => { if (alive) setRequest(null) })
    return () => { alive = false }
  }, [currentCase?.id])

  if (!currentCase) return <Navigate to="/home" replace />
  if (request === null) return <Navigate to="/case/authorizations" replace />

  const pick = async (e) => {
    const f = e.target.files?.[0]
    if (e.target) e.target.value = ''
    if (!f || !request) return
    setError(''); setFileName(f.name); setPhase('reading')
    try {
      const up = await referralRequestsAPI.uploadDocument(request.id, f)
      setDocumentKey(up?.documentKey || '')
      let ex = null
      try { ex = up?.documentKey ? await aiAPI.extractDocument(currentCase.id, up.documentKey, f.name, 'auto') : null } catch (_) { ex = null }
      if (ex && ex.success) {
        setFields((prev) => ({
          authNumber: ex.authorizationNumber || prev.authNumber,
          referredTo: ex.referredTo || ex.referredToDoctor || prev.referredTo || doctorName,
          visits: ex.visitsRemaining != null && ex.visitsRemaining !== '' ? String(ex.visitsRemaining) : prev.visits,
          validTo: ex.expirationDate || ex.validThrough || prev.validTo,
        }))
      } else {
        setFields((prev) => ({ ...prev, referredTo: prev.referredTo || doctorName }))
      }
      setPhase('review')
    } catch (err) {
      setError(err?.message || t('referral.sendFailed', "We couldn't send your request. Please try again."))
      setPhase('pick')
    }
  }

  const save = async () => {
    if (!request || phase === 'saving') return
    setPhase('saving'); setError('')
    try {
      await referralRequestsAPI.answer(request.id, { documentKey, fields })
      setPhase('saved')
    } catch (err) {
      setError(err?.error || err?.message || t('referral.sendFailed', "We couldn't send your request. Please try again."))
      setPhase('review')
    }
  }

  if (phase === 'saved') {
    return (
      <div className="min-h-full bg-gray-50 flex flex-col" data-testid="referral-on-file">
        <CompactHeader stepLabel={t('referral.stepOnFile', 'Referral on file')} onBack={() => navigate('/case/home', { replace: true })} onNotification={() => navigate('/notifications')} />
        <div className="flex-1 px-4 pt-4 pb-6 flex flex-col gap-3">
          <div style={card} className="flex flex-col gap-2">
            <p className="m-0 text-[16px] font-extrabold" style={{ color: NAVY }}>{t('referral.cardTitle', { defaultValue: 'Referral to {{doctor}}', doctor: doctorName })}</p>
            <div className="flex gap-2.5 text-[13.5px] leading-snug" style={{ color: NAVY }}>
              <span className="flex-none w-[22px] h-[22px] rounded-full grid place-items-center text-white" style={{ background: '#15803D' }}><Check size={13} /></span>
              <span>{t('referral.stepOnFileDetail', { defaultValue: 'On file · auth #{{auth}} · {{visits}} visits until {{until}}', auth: fields.authNumber, visits: fields.visits, until: fields.validTo })}</span>
            </div>
            <span className="self-start text-[12px] font-bold rounded-full px-2.5 py-1" style={{ background: '#E3F6E8', color: '#166534' }}>{t('referral.stepOnFile', 'Referral on file')}</span>
          </div>
          <button type="button" onClick={() => navigate('/case/home', { replace: true })} data-cta="referral-book"
                  className="w-full py-3.5 rounded-[14px] text-[15px] font-extrabold text-white" style={{ background: '#15803D' }}>
            {t('referral.bookCta', { defaultValue: 'Book with {{doctor}}', doctor: doctorName })}
          </button>
          <p className="m-0 text-[13px] leading-snug" style={{ color: MUTED }}>{t('referral.checkinNote', 'At check-in the referral shows as your authorization, ready to confirm with one tap.')}</p>
        </div>
      </div>
    )
  }

  const input = 'rounded-[10px] px-2.5 py-2 text-[14px] font-semibold bg-white'
  return (
    <div className="min-h-full bg-gray-50 flex flex-col" data-testid="referral-upload">
      <CompactHeader stepLabel={t('referral.uploadTitle', 'Upload your referral')} onBack={() => caseBack(navigate)} onNotification={() => navigate('/notifications')} />
      <div className="flex-1 px-4 pt-4 pb-6 flex flex-col gap-3">
        <input ref={fileRef} type="file" accept="application/pdf,image/jpeg,image/png,image/heic,image/heif" className="hidden" onChange={pick} />
        <button type="button" onClick={() => fileRef.current && fileRef.current.click()} disabled={request === undefined || phase === 'reading'}
                data-cta="referral-pick-file" className="flex items-center gap-3 text-left rounded-2xl p-4 bg-white disabled:opacity-60"
                style={{ border: '1.5px dashed #9FB3C6' }}>
          <span className="w-12 h-[60px] rounded-md grid place-items-center flex-none" style={{ background: '#E9F8FF', color: NAVY }}>
            {phase === 'reading' || request === undefined ? <OrellaSpinner size={18} tone="inherit" /> : fileName ? <FileText size={20} /> : <Upload size={20} />}
          </span>
          <span className="min-w-0">
            <b className="block text-[14px] truncate" style={{ color: NAVY }}>{fileName || t('referral.uploadTitle', 'Upload your referral')}</b>
            {phase === 'review' && <span className="block text-[13px]" style={{ color: MUTED }}>{t('referral.readNote', 'Read just now · check the details below')}</span>}
          </span>
        </button>
        {(phase === 'review' || phase === 'saving') && (
          <div style={card} className="flex flex-col gap-2.5">
            <span className="text-[11px] font-bold tracking-[.08em] uppercase" style={{ color: MUTED }}>{t('referral.fromReferral', 'From your referral')}</span>
            <label className="flex flex-col gap-1 text-[12.5px] font-bold" style={{ color: NAVY }}>{t('referral.authNumber', 'Authorization / referral number')}
              <input className={input} style={{ border: '1px solid #B7C7D6', color: NAVY }} value={fields.authNumber} onChange={(e) => setFields({ ...fields, authNumber: e.target.value })} data-testid="referral-auth-number" />
            </label>
            <label className="flex flex-col gap-1 text-[12.5px] font-bold" style={{ color: NAVY }}>{t('referral.referredTo', 'Referred to')}
              <input className={input} style={{ border: '1px solid #B7C7D6', color: NAVY }} value={fields.referredTo} onChange={(e) => setFields({ ...fields, referredTo: e.target.value })} />
            </label>
            <div className="grid grid-cols-2 gap-2.5">
              <label className="flex flex-col gap-1 text-[12.5px] font-bold min-w-0" style={{ color: NAVY }}>{t('referral.visits', 'Visits')}
                <input className={input} inputMode="numeric" style={{ border: '1px solid #B7C7D6', color: NAVY }} value={fields.visits} onChange={(e) => setFields({ ...fields, visits: e.target.value })} />
              </label>
              <label className="flex flex-col gap-1 text-[12.5px] font-bold min-w-0" style={{ color: NAVY }}>{t('referral.validUntil', 'Valid until')}
                <input className={input} style={{ border: '1px solid #B7C7D6', color: NAVY }} value={fields.validTo} onChange={(e) => setFields({ ...fields, validTo: e.target.value })} />
              </label>
            </div>
            <p className="m-0 text-[13px] leading-snug" style={{ color: MUTED }}>{t('referral.codesNote', "Diagnosis and procedure codes are kept with the document for your doctor's office.")}</p>
          </div>
        )}
        {error && <p className="m-0 text-[13px] font-semibold" style={{ color: '#B91C1C' }} role="alert">{error}</p>}
        <button type="button" onClick={save} disabled={!(phase === 'review' && fields.authNumber.trim())} data-cta="referral-save"
                className="mt-auto w-full py-3.5 rounded-[14px] text-[15px] font-extrabold text-white flex items-center justify-center gap-2 disabled:opacity-50"
                style={{ background: '#15803D' }}>
          {phase === 'saving' && <OrellaSpinner size={16} tone="inherit" />}
          {t('referral.save', 'Save referral')}
        </button>
      </div>
    </div>
  )
}
